import { api } from './client';
import type { DivisionRole } from './tasks.api';

/**
 * Устройство отдела: состав, группы, проекты, паблики.
 *
 * Отдельно от tasks.api, потому что это другая частота: задачи перечитывают
 * каждые несколько минут, а состав — раз в месяц, когда кого-то нанимают.
 */

export interface DivisionMember {
  id: string;
  name: string | null;
  username: string | null;
  avatarUrl?: string | null;
  role: DivisionRole;
  canPublish: boolean;
  /** Монтажёр: мимо модератора. */
  skipModeration?: boolean;
  /** Монтажёр: публикует сданное сам, без приёмки. */
  selfPublish?: boolean;
  /** В какой группе отдела человек работает. У руководителя и модератора пусто. */
  groupId: string | null;
  telegramLinked: boolean;
}

/** Группа отдела: контент-мейкер и его монтажёры. Астана, Алматы. */
export interface DivisionGroup {
  id: string;
  name: string;
  slug: string;
  contentMakerId: string | null;
  chatId: number | null;
}

export interface DivisionPublic {
  id: string;
  name: string;
  slug: string;
  /** Группа, которая ведёт паблик. `null` — общий для всех групп проекта. */
  leadGroupId: string | null;
  accountIds: string[];
}

export interface DivisionProject {
  id: string;
  name: string;
  slug: string;
  archived: boolean;
  publics: DivisionPublic[];
  /** Какие группы работают над проектом. */
  groupIds: string[];
  /** Кто его модерирует. */
  moderatorIds: string[];
  /** Куда команды выкладывают ролики направления. Аккаунт бывает в нескольких. */
  accountIds?: string[];
  /** Постановщики и монтажёры направления (с 08.10.2026). */
  memberIds?: { userId: string; role: 'content_maker' | 'editor' }[];
  /** Уходят ли принятые ролики в Telegram-бэкап. */
  backup?: boolean;
  /** Telegram-канал направления; null — канал команды монтажёра. */
  chatId?: number | null;
  /** Аккаунты, где ролик смотрит модератор направления. */
  moderatedAccountIds?: string[];
  /** Без приёмки: монтажёр публикует сданное сам. */
  selfPublish?: boolean;
}

export interface DivisionOverview {
  division: { id: string; name: string; slug: string; backupChatId: number | null };
  members: DivisionMember[];
  groups: DivisionGroup[];
  projects: DivisionProject[];
}

/** Строка списка отделов — экран платформенного админа. */
export interface DivisionRow {
  id: string;
  name: string;
  slug: string;
  members: number;
  groups: number;
  projects: number;
  heads: { id: string; name: string | null }[];
}

export const divisionApi = {
  getOverview: (divisionId: string) =>
    api.get<DivisionOverview>(`/division/${divisionId}/overview`),

  getCandidates: (divisionId: string) =>
    api.get<{ candidates: { id: string; name: string; username: string }[] }>(
      `/division/${divisionId}/candidates`,
    ),

  // ── отделы: только платформенный админ ──────────────────────────────
  list: () => api.get<{ divisions: DivisionRow[] }>('/division'),

  create: (body: { name: string; slug?: string; headId?: string }) =>
    api.post<{ division: { id: string; name: string; slug: string }; warning?: string }>('/division', body),

  rename: (divisionId: string, name: string) =>
    api.patch<{ ok: true }>(`/division/${divisionId}`, { name }),

  // ── состав ──────────────────────────────────────────────────────────
  addMember: (divisionId: string, body: { userId: string; role: DivisionRole; groupId?: string }) =>
    api.post<{ ok: true }>(`/division/${divisionId}/members`, body),

  updateMember: (
    divisionId: string,
    userId: string,
    body: { role?: DivisionRole; groupId?: string | null; canPublish?: boolean; skipModeration?: boolean; selfPublish?: boolean },
  ) => api.patch<{ ok: true }>(`/division/${divisionId}/members/${userId}`, body),

  removeMember: (divisionId: string, userId: string) =>
    api.delete<{ ok: true }>(`/division/${divisionId}/members/${userId}`),

  // ── группы ──────────────────────────────────────────────────────────
  createGroup: (divisionId: string, body: { name: string; contentMakerId?: string; chatId?: number }) =>
    api.post<{ group: DivisionGroup }>(`/division/${divisionId}/groups`, body),

  updateGroup: (groupId: string, body: { name?: string; contentMakerId?: string | null; chatId?: number | null }) =>
    api.patch<{ ok: true }>(`/division/groups/${groupId}`, body),

  deleteGroup: (groupId: string) => api.delete<{ ok: true }>(`/division/groups/${groupId}`),

  // ── проекты и паблики ───────────────────────────────────────────────
  createProject: (divisionId: string, body: { name: string }) =>
    api.post<{ project: DivisionProject }>(`/division/${divisionId}/projects`, body),

  updateProject: (projectId: string, body: { name?: string; archived?: boolean; backup?: boolean; chatId?: number | null; selfPublish?: boolean }) =>
    api.patch<{ ok: true }>(`/division/projects/${projectId}`, body),

  createPublic: (projectId: string, body: { name: string }) =>
    api.post<{ public: DivisionPublic }>(`/division/projects/${projectId}/publics`, body),

  /** `leadGroupId: null` — паблик снова общий для всех групп проекта. */
  updatePublic: (publicId: string, body: { name?: string; leadGroupId?: string | null }) =>
    api.patch<{ ok: true }>(`/division/publics/${publicId}`, body),

  attachAccount: (publicId: string, accountId: string) =>
    api.post<{ ok: true }>(`/division/publics/${publicId}/accounts`, { accountId }),

  detachAccount: (publicId: string, accountId: string) =>
    api.delete<{ ok: true }>(`/division/publics/${publicId}/accounts/${encodeURIComponent(accountId)}`),

  // ── кто работает над проектом ───────────────────────────────────────
  addProjectGroup: (projectId: string, groupId: string) =>
    api.post<{ ok: true }>(`/division/projects/${projectId}/groups`, { groupId }),

  removeProjectGroup: (projectId: string, groupId: string) =>
    api.delete<{ ok: true }>(`/division/projects/${projectId}/groups/${groupId}`),

  addProjectModerator: (projectId: string, userId: string) =>
    api.post<{ ok: true }>(`/division/projects/${projectId}/moderators`, { userId }),

  removeProjectModerator: (projectId: string, userId: string) =>
    api.delete<{ ok: true }>(`/division/projects/${projectId}/moderators/${userId}`),

  // ── люди направления ────────────────────────────────────────────────
  addProjectMember: (projectId: string, userId: string) =>
    api.post<{ ok: true }>(`/division/projects/${projectId}/members`, { userId }),

  removeProjectMember: (projectId: string, userId: string) =>
    api.delete<{ ok: true }>(`/division/projects/${projectId}/members/${userId}`),

  // ── аккаунты направления ────────────────────────────────────────────
  addProjectAccount: (projectId: string, accountId: string) =>
    api.post<{ ok: true }>(`/division/projects/${projectId}/accounts`, { accountId }),

  /** Модерация на аккаунте направления: вкл/выкл. */
  moderateProjectAccount: (projectId: string, accountId: string, moderated: boolean) =>
    api.patch<{ ok: true }>(`/division/projects/${projectId}/accounts/${encodeURIComponent(accountId)}`, { moderated }),

  removeProjectAccount: (projectId: string, accountId: string) =>
    api.delete<{ ok: true }>(`/division/projects/${projectId}/accounts/${encodeURIComponent(accountId)}`),
};
